import React, { lazy, Suspense, Component } from 'react';
import PropTypes from 'prop-types';

import { noop } from 'lodash';
import moment from 'moment';
import envConfig from 'trade-in-360/config/index.js';
import { LocaleService, ConfigsService, WindowProxy } from '@sm360/widget-commons-core-services';
import Logger from '@sm360/logger-sentry';
import {
    NoSSR,
    Account,
    LoaderFullScreen,
    ProfileService,
    EVENTS,
    REDIRECTIONS,
    AccountService,
    UtilsService,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { Wizard } from '@sm360/widget-commons-forms';
import { Quote, PurchaseMethods } from '@sm360/widget-commons-vehicle';
import { StepBar, ProgressBar } from '@sm360/ui-library';

import TradeInModel from 'trade-in-360/model/TradeInModel.js';
import TradeInModelNew from 'trade-in-360/model/TradeInModelNew.js';
import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import StepHeader from 'trade-in-360/components/InstantEstimateWrapper/Components/StepHeader.jsx';
import VehicleInfo from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleInfo/VehicleInfo.jsx';
import VehicleBasicInfo from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleBasicInfo/VehicleBasicInfo.jsx';
import VehicleCondition from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleCondition/VehicleCondition.jsx';
import ContactInformations from 'trade-in-360/components/InstantEstimateWrapper/Steps/ContactInformations/ContactInformations.jsx';
import VehicleOptions from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleOptions/VehicleOptions.jsx';
import ErrorInspection from 'trade-in-360/components/InstantEstimateWrapper/Steps/ErrorInspection/ErrorInspection.jsx';
import VehicleAppraisal from 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleAppraisal/VehicleAppraisal.jsx';
import AppointmentForm from 'trade-in-360/components/InstantEstimateWrapper/Steps/AppointmentForm/AppointmentForm.jsx';
import AppointmentFormTrade360 from 'trade-in-360/components/InstantEstimateWrapper/Steps/AppointmentFormTrade360/AppointmentFormTrade360.jsx';
import OfficialEvaluation from 'trade-in-360/components/InstantEstimateWrapper/Steps/OfficialEvaluation/OfficialEvaluation.jsx';
import VehicleTypeLabel from 'trade-in-360/components/InstantEstimateWrapper/Components/VehicleTypeLabel/VehicleTypeLabel.jsx';
import SLIDER from 'trade-in-360/constants/slider.js';
import ShowroomCBBService from 'trade-in-360/services/ShowroomCBBService/ShowroomCBBService.js';
import TradeInVehicleService from 'trade-in-360/services/TradeInVehicleService/TradeInVehicleService.js';

import 'trade-in-360/components/InstantEstimateWrapper/InstantEstimateWrapper.scss';
import WIDGET_VERSION from 'trade-in-360/constants/widgetVersions.js';
import WIZARD_STEPS, { STEP_NAMES, STEP_HEADER_VISIBILITY } from 'trade-in-360/constants/wizardSteps.js';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import StepsHeader from '../StepsHeader/StepsHeader.jsx';
import TRADE_OPTIONS from '../../constants/tradeOptions.js';
import NewTradeInContext from '../../NewTradeInContext';
import Checkmark from '../../assets/images/Checkmark.jsx';
import { getBeforeUnloadHandler } from '../InstantEstimateWrapper/Utils/utils.js';

// The webpackChunkName name here is intended to be public. Webpack will use it to name files in production and development modes.
const CanadianBlackBookEstimate = lazy(() =>
    import(/* webpackChunkName: "CanadianBlackBookEstimate" */ 'trade-in-360/components/CanadianBlackBookEstimate/CanadianBlackBookEstimate.jsx')
);

const { PurchaseMethodsActions } = PurchaseMethods;
class InstantEstimateWrapper extends Component {
    // eslint-disable-next-line react/sort-comp
    static contextType = NewTradeInContext;

    constructor(props, context) {
        super(props);

        const { isTradeInAppointment, isOfficialTradeIn, isViewOnly } = context || {};

        const initialIsLoading =
            AccountService.isAuthenticated() &&
            !props.tradeInVehicle.id &&
            (isOfficialTradeIn || isTradeInAppointment || isViewOnly);

        this.state = {
            isScreenLock: false,
            isHeaderShown: true,
            tradeInVehicle: props.tradeInVehicle,
            tradeInValues: null,
            errorInspection: {},
            questionsModel: {},
            account: new Account(),
            index: 0,
            lastIndex: 0,
            inspectionNeeded: false,
            previousStep: '',
            isModalCloseShown: true,
            isLoading: initialIsLoading,
            appointmentDate: '',
            displayConfirmation: true,
            isAppointmentConfirmation: false,
            config: props.config,
        };

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());
        this.previousContext = null;

        this.getSteps = this.getSteps.bind(this);
        this.getListOfSteps = this.getListOfSteps.bind(this);
        this.getStepNumber = this.getStepNumber.bind(this);
        this.getStepName = this.getStepName.bind(this);
        this.toggleScreenLock = this.toggleScreenLock.bind(this);
        this.updateLeadTrade360Version = this.updateLeadTrade360Version.bind(this);
        this.updateLeadDilawriVersion = this.updateLeadDilawriVersion.bind(this);
        this.updateTradeInVehicle = this.updateTradeInVehicle.bind(this);
        this.onClickStep = this.onClickStep.bind(this);
        this.goToStep = this.goToStep.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onBack = this.onBack.bind(this);
        this.onErrorBack = this.onErrorBack.bind(this);
        this.onError = this.onError.bind(this);
        this.onContactInformationDone = this.onContactInformationDone.bind(this);
        this.onMakeAppointmentHandler = this.onMakeAppointmentHandler.bind(this);
        this.setInspectionNeeded = this.setInspectionNeeded.bind(this);
        this.handleEstimateChange = this.handleEstimateChange.bind(this);
        this.handleEstimateChangeSlider = this.handleEstimateChangeSlider.bind(this);
        this.handleConfirmationMessageClose = this.handleConfirmationMessageClose.bind(this);
        this.beforeUnloadHandler = getBeforeUnloadHandler(
            context.version === WIDGET_VERSION.TRADE_360 && !props.isModal && context.isModified
        ).bind(this);
        this.saveTradeIn = this.saveTradeIn.bind(this);
        this.checkStepEligibility = this.checkStepEligibility.bind(this);
        this.refreshBrowserQuitModalListener = this.refreshBrowserQuitModalListener.bind(this);
        this.imagesServer = envConfig.source.image;
    }

    componentDidMount() {
        const { fieldsLocked, setFieldsLocked, version } = this.context || {};

        if (version === WIDGET_VERSION.TRADE_360) this.checkStepEligibility();

        if (fieldsLocked) {
            setFieldsLocked(false);
        }

        // Store previous context to watch for context changes
        this.previousContext = this.context;

        // Trigger the browser confirmation popup to prevent user from losing the trade-in data (only for Trade360)
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    componentDidUpdate(prevProps) {
        const { isOfficialTradeIn, isTradeInAppointment, version, isNewRequest, useCBB, isViewOnly, officialEdit } =
            this.context || {};

        if (version === WIDGET_VERSION.TRADE_360) {
            // If tradeInVehicle.id passed in prop changes: use it in local state
            if (prevProps.tradeInVehicle.id !== this.props.tradeInVehicle.id && !isNewRequest) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ tradeInVehicle: this.props.tradeInVehicle });

                if (this.props.tradeInVehicle.id) {
                    if (isViewOnly) {
                        if (useCBB) this.goToStep(STEP_NAMES.CBB_ESTIMATE);
                        else this.goToStep(STEP_NAMES.OFFICIAL_EVALUATION);
                    } else if (isOfficialTradeIn && !officialEdit && !this.state.isAppointmentConfirmation) {
                        this.goToStep(STEP_NAMES.OFFICIAL_EVALUATION);
                    } else if (isTradeInAppointment) {
                        this.goToStep(STEP_NAMES.APPOINTMENT_FORM);
                    }
                    // eslint-disable-next-line react/no-did-update-set-state
                    this.setState({ isLoading: false });
                }

                // If we delete the tradeIn from account (tradeInVehicle.id is empty)
                // we want to ensure we redirect to first screen
                this.checkStepEligibility();
            }

            // Update the browser confirmation listener
            if (this.previousContext?.isModified !== this.context?.isModified) {
                this.refreshBrowserQuitModalListener();
            }

            // Update previousContext value
            this.previousContext = this.context;
        }
    }

    // Refresh the event listener in charge of enabling the browser confirmation modal
    refreshBrowserQuitModalListener(forceEnable = true) {
        const shouldActivateBrowserQuitModal =
            forceEnable &&
            (this.context.version === WIDGET_VERSION.TRADE_360 && !this.props.isModal && this.context.isModified);
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.beforeUnloadHandler = getBeforeUnloadHandler(shouldActivateBrowserQuitModal);
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    /* Check if user has the right to be in appointment or official step
     * Redirect to first screen after 3s if no tradeIn is found in the logged account
    */
    checkStepEligibility() {
        const { isOfficialTradeIn, isTradeInAppointment, setFieldsLocked, setIsOfficialTradeIn } = this.context || {};
        if (isOfficialTradeIn || isTradeInAppointment) {
            setTimeout(() => {
                if (!this.props.tradeInVehicle.id) {
                    setFieldsLocked(false);
                    setIsOfficialTradeIn(false);
                    this.goToStep(STEP_NAMES.VEHICLE_INFO);
                }
                if (this.state.isLoading) this.setState({ isLoading: false });
            }, 5000);
        }
    }

    /* TODO: deprecate onClickStep() */
    goToStep(stepName, previousStep) {
        const stepNo = this.getStepNumber(stepName);
        const isHeaderShown = !!STEP_HEADER_VISIBILITY?.[stepName];

        this.setState({
            index: stepNo,
            isHeaderShown,
            previousStep,
            isModalCloseShown: true,
        });
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    onClickStep(i) {
        this.setState({
            index: i,
        });
    }
    onDone() {
        this.setState((prevState) => {
            const index = prevState.index + 1;
            const stepName = this.getStepName(index);
            const isHeaderShown = !!STEP_HEADER_VISIBILITY?.[stepName];
            return { index, isHeaderShown };
        });
    }
    onBack() {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_BACK_BTN());
        this.setState((prevState) => {
            const index = prevState.index - 1;
            const stepName = this.getStepName(index);
            const isHeaderShown = !!STEP_HEADER_VISIBILITY?.[stepName];
            return { index, isHeaderShown };
        });
    }

    onErrorBack() {
        this.setState((prevState) => ({
            isHeaderShown: true,
            index: prevState.lastIndex,
        }));
    }

    onError(questionError, answerError, questionErrorId, currentQuestionsAnswers) {
        this.setState((prevState) => ({
            index: 6, // HARD-CODED- has to be the last step (i.e. Inspection Needed screen)
            lastIndex: prevState.index,
            isHeaderShown: false,
            errorInspection: { questionError, answerError, questionErrorId, currentQuestionsAnswers },
        }));
    }

    onContactInformationDone() {
        if (this.context.useCBB && !this.context.skipEstimate && !this.context.officialEdit)
            this.goToStep(STEP_NAMES.CBB_ESTIMATE);
        else this.goToStep(STEP_NAMES.OFFICIAL_EVALUATION);
    }

    onMakeAppointmentHandler(from) {
        return this.props.appointmentEnabled
            ? () => {
                  this.goToStep(STEP_NAMES.APPOINTMENT_FORM, from);
              }
            : null;
    }

    handleConfirmationMessageClose() {
        if (this.props.isModal) {
            this.props.closeModal({ confirm: false });
        } else {
            this.context.setIsModified(false);
            this.refreshBrowserQuitModalListener(false);

            if (this.props.onTradeInSuccess && this.props.onTradeInSuccess !== noop && this.props.quote) {
                this.props.onTradeInSuccess(this.props.quote);
            } else {
                // Redirect to inventory page
                const windowProxy = WindowProxy.get();
                if (windowProxy) {
                    windowProxy.location.assign(REDIRECTIONS[LocaleService.getSimpleLocale()].listingNew);
                }
            }
        }
    }

    setInspectionNeeded(tradeInVehicle) {
        this.setState((prevState) => ({
            index: 3, // Contact screen
            lastIndex: prevState.index,
            inspectionNeeded: true,
            tradeInVehicle: { ...prevState.tradeInVehicle, tradeInVehicle },
        }));
    }

    dispatchTradeInCreatedEvent = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_CREATED));
        }
    };

    getListOfSteps() {
        let steps = [];
        const { useCBB, version } = this.context || {};

        if (version === WIDGET_VERSION.TRADE_360) {
            steps = [
                STEP_NAMES.VEHICLE_INFO,
                STEP_NAMES.CONTACT_INFORMATION,
                STEP_NAMES.CBB_ESTIMATE,
                STEP_NAMES.OFFICIAL_EVALUATION,
                STEP_NAMES.APPOINTMENT_FORM,
            ];
            // Remove CBB estimate if official evaluation or if client doesn't pay for it
            if (!useCBB) steps = steps.filter((step) => step !== STEP_NAMES.CBB_ESTIMATE);
            if (!this.props.appointmentEnabled) steps = steps.filter((step) => step !== STEP_NAMES.APPOINTMENT_FORM);
        } else if (version === WIDGET_VERSION.DILAWRI) {
            steps = [
                STEP_NAMES.VEHICLE_INFO,
                STEP_NAMES.VEHICLE_OPTIONS,
                STEP_NAMES.VEHICLE_CONDITION,
                STEP_NAMES.CONTACT_INFORMATION,
                STEP_NAMES.VEHICLE_APPRAISAL,
                STEP_NAMES.APPOINTMENT_FORM,
                STEP_NAMES.ERROR_INSPECTION,
            ];
        }
        return steps;
    }

    getStepNumber(stepName) {
        const steps = this.getListOfSteps();
        return steps.indexOf(stepName);
    }

    getStepName(index) {
        const steps = this.getListOfSteps();
        return steps[index];
    }

    // eslint-disable-next-line no-unused-vars
    getSteps(version, isModal) {
        const steps = [];

        if (version === WIDGET_VERSION.TRADE_360) {
            const { useCBB } = this.context || {};
            // Vehicle Selection Step (Screen 1)
            if (useCBB) {
                steps.push(() => (
                    <VehicleInfo
                        autofillTradeInVehicleFromAccount={false}
                        account={this.state.account}
                        updateAccount={(account) => this.setState({ account })}
                        lockScreen={this.toggleScreenLock}
                        onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                        onNextStep={this.onDone}
                        onTradeInVehicleUpdated={this.updateTradeInVehicle}
                        tradeInVehicle={this.state.tradeInVehicle}
                        isRenderedInModal
                        useCBB
                        quote={this.props.quote}
                        displayOptionsOnInfoPage
                        config={this.props.config}
                    />
                ));
            } else {
                steps.push(() => (
                    <VehicleBasicInfo
                        tradeInVehicle={this.state.tradeInVehicle}
                        onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                        onNextStep={this.onDone}
                    />
                ));
            }

            // Contact Info Step (Screen 2)
            steps.push(() => (
                <ContactInformations
                    tradeInVehicle={this.state.tradeInVehicle}
                    onPreviousStep={this.onBack}
                    onNextStep={this.onContactInformationDone}
                    onTradeInVehicleUpdated={this.updateTradeInVehicle}
                    onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onContactInformationDone)}
                    inspectionNeeded={this.state.inspectionNeeded}
                    errorInspection={this.state.errorInspection}
                    closeModal={() => this.props.closeModal({ confirm: false })}
                    quote={this.props.quote}
                    isPhoneRequired
                    displayPolicyLabel
                    config={this.props.config}
                />
            ));

            // Appraisal Min Max (Screen 3)
            if (useCBB) {
                steps.push(() => (
                    <NoSSR>
                        <Suspense fallback="">
                            <CanadianBlackBookEstimate
                                config={this.props.config}
                                account={this.state.account}
                                tradeInVehicle={this.state.tradeInVehicle}
                                tradeInValues={{
                                    amount: this.state.tradeInVehicle?.estimation?.amount,
                                    high: this.state.tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.high,
                                    low: this.state.tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.low,
                                    taxesFactor: this.state?.tradeInVehicle?.estimation?.taxesFactor,
                                }}
                                isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                                handleEstimateChange={this.handleEstimateChangeSlider}
                                saveTradeIn={this.saveTradeIn}
                                onMakeAppointment={this.onMakeAppointmentHandler(STEP_NAMES.CBB_ESTIMATE)}
                                onOfficialEvaluation={() =>
                                    this.goToStep(STEP_NAMES.OFFICIAL_EVALUATION, STEP_NAMES.CBB_ESTIMATE)
                                }
                                onPreviousStep={() => this.goToStep(STEP_NAMES.VEHICLE_INFO)}
                                onSaveConfirmationMessage={() =>
                                    this.setState({
                                        isHeaderShown: false,
                                        isModalCloseShown: true,
                                        displayConfirmation: true,
                                    })
                                }
                                closeText={
                                    this.props.appointmentEnabled && this.props.isShowroomV2Enabled
                                        ? null
                                        : LocaleService.t(`TRADE_IN.BUTTONS.DONE`)
                                }
                                onClose={this.handleConfirmationMessageClose}
                            />
                        </Suspense>
                    </NoSSR>
                ));
            }
            // Appointment Form Step (Screen 4)
            steps.push(() => (
                <OfficialEvaluation
                    tradeInVehicle={this.state.tradeInVehicle}
                    quote={this.props.quote}
                    onPreviousStep={() => this.goToStep(this.state?.previousStep || STEP_NAMES.VEHICLE_INFO)}
                    onNextStep={(isAppointmentScheduled) =>
                        this.setState(
                            { isModalCloseShown: true, displayConfirmation: !isAppointmentScheduled },
                            this.dispatchTradeInCreatedEvent
                        )
                    }
                    previousLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_INFO')}
                    onMakeAppointment={this.onMakeAppointmentHandler(STEP_NAMES.OFFICIAL_EVALUATION)}
                    closeText={LocaleService.t(`TRADE_IN.BUTTONS.DONE`)}
                    onClose={this.handleConfirmationMessageClose}
                    onChange={() => this.context.setIsModified(true)}
                    appointmentDate={this.state.appointmentDate}
                    config={this.props.config}
                />
            ));
            // Appointment Form Step (Screen 5)
            if (this.props.appointmentEnabled) {
                steps.push(() => (
                    <AppointmentFormTrade360
                        tradeInVehicle={this.state.tradeInVehicle}
                        quote={this.props.quote}
                        onNextStep={(isEvaluationCompleted) =>
                            this.setState({
                                isModalCloseShown: true,
                                isAppointmentConfirmation: true, // Indicate the previous page for Official evaluation
                                displayConfirmation: !isEvaluationCompleted,
                            })
                        }
                        onPreviousStep={() => this.goToStep(this.state?.previousStep || STEP_NAMES.VEHICLE_INFO)}
                        onOfficialEvaluation={(appointmentDate) => this.onOfficialEvaluation(appointmentDate)}
                        closeText={LocaleService.t(`TRADE_IN.BUTTONS.DONE`)}
                        onClose={this.handleConfirmationMessageClose}
                        saveTradeIn={this.saveTradeIn}
                        onChange={() => this.context.setIsModified(true)}
                        isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                        config={this.props.config}
                    />
                ));
            }
        } else if (version === WIDGET_VERSION.DILAWRI) {
            // Vehicle Selection Step (Screen 1)
            steps.push(() => (
                <VehicleInfo
                    autofillTradeInVehicleFromAccount={false}
                    account={this.state.account}
                    updateAccount={(account) => this.setState({ account })}
                    lockScreen={this.toggleScreenLock}
                    onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                    onNextStep={this.onDone}
                    onTradeInVehicleUpdated={this.updateTradeInVehicle}
                    tradeInVehicle={this.state.tradeInVehicle}
                    isRenderedInModal
                    useCBB
                    quote={this.props.quote}
                    nextLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS')}
                    config={this.state.config}
                />
            ));

            // Vehicle Option Step (Screen 2)
            steps.push(() => (
                <VehicleOptions
                    onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                    tradeInVehicle={this.state.tradeInVehicle}
                    onPreviousStep={this.onBack}
                    onNextStep={this.onDone}
                    previousLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_INFO')}
                    nextLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION')}
                />
            ));
            // Vehicle Condition Step (Screen 3) - Component has to be created
            steps.push(() => (
                <VehicleCondition
                    parentModel={this.state.questionsModel}
                    parentRawModel={this.state.parentRawModel}
                    updateParentModel={(questionsModel, parentRawModel) =>
                        this.setState({ questionsModel, parentRawModel })
                    }
                    tradeInVehicle={this.state.tradeInVehicle}
                    onPreviousStep={this.onBack}
                    onNextStep={this.onDone}
                    onTradeInVehicleUpdated={this.updateTradeInVehicle}
                    onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                    onError={this.onError}
                    questionErrorId={this.state.errorInspection.questionErrorId}
                    previousLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS')}
                    nextLabel={LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}
                    config={this.state.config}
                />
            ));

            // Contact Info Step (Screen 4)
            steps.push(() => (
                <ContactInformations
                    tradeInVehicle={this.state.tradeInVehicle}
                    onPreviousStep={this.onBack}
                    onNextStep={() => this.setState({ isHeaderShown: false }, this.onDone)}
                    onTradeInVehicleUpdated={this.updateTradeInVehicle}
                    onFormSubmit={(tradeInVehicle, orgUnitId) =>
                        this.setState(
                            {
                                tradeInVehicle,
                                isHeaderShown: false,
                                config: { ...this.state.config, orgUnitId },
                            },
                            this.onDone
                        )
                    }
                    inspectionNeeded={this.state.inspectionNeeded}
                    errorInspection={this.state.errorInspection}
                    closeModal={() => this.props.closeModal({ confirm: false })}
                    quote={this.props.quote}
                    previousLabel={LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION')}
                    config={this.state.config}
                    isMultiDealerTradeIn={this.props.isMultiDealerTradeIn}
                    websiteDomain={this.props.websiteDomain}
                    sessionUserGeoData={this.props.sessionUserGeoData}
                />
            ));

            // Estimation Value (Screen 5)
            steps.push(() => (
                <VehicleAppraisal
                    tradeInVehicle={this.state.tradeInVehicle}
                    onTradeInVehicleUpdated={this.updateTradeInVehicle}
                    onPreviousStep={() => {
                        this.setState({ isHeaderShown: true }, this.onBack);
                    }}
                    onMakeAppointment={() => this.setState({ isHeaderShown: false }, this.onDone)}
                    closeModal={() => this.props.closeModal({ confirm: false })}
                    account={this.state.account}
                    handleEstimateChange={this.updateLeadDilawriVersion}
                    previousLabel={LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}
                    config={this.state.config}
                />
            ));

            // Appointment Form Step (Screen 6)
            steps.push(() => (
                <AppointmentForm
                    tradeInVehicle={this.state.tradeInVehicle}
                    quote={this.props.quote}
                    closeModal={() => this.props.closeModal({ confirm: false })}
                    config={this.state.config}
                />
            ));

            // Error: inspection needed (Screen 7 - temporary)
            steps.push(() => (
                <ErrorInspection
                    tradeInVehicle={this.state.tradeInVehicle}
                    error={this.state.errorInspection}
                    onPreviousStep={this.onErrorBack}
                    onNextStep={this.setInspectionNeeded}
                    config={this.props.config}
                />
            ));
        }

        return steps;
    }

    getNavigationSteps(version) {
        const wizardSteps = WIZARD_STEPS?.[version] || [];
        return wizardSteps.map((step) => {
            const label = LocaleService.t(step.i18n_key);
            return { label: <StepHeader label={label} iconClass={step.iconClass} />, name: label };
        });
    }

    toggleScreenLock() {
        this.setState((prevState) => {
            return { isScreenLock: !prevState.isScreenLock };
        });
    }

    updateTradeInVehicle(tradeInVehicle, onStateSet = noop) {
        const isTradeInVehicleValid = !!tradeInVehicle?.id;
        this.context.setIsModified(isTradeInVehicleValid);

        this.setState((oldState) => {
            return { ...oldState, tradeInVehicle };
        }, onStateSet);
    }

    onOfficialEvaluation(appointmentDate) {
        if (appointmentDate) this.setState({ appointmentDate });
        this.goToStep(STEP_NAMES.OFFICIAL_EVALUATION);
    }

    // For Trade360 Version use this function to update the trade-in lead
    async updateLeadTrade360Version(amount, existingAccount = false) {
        const { tradeInVehicle } = this.state;
        let accountObject = {};
        try {
            const account = await ProfileService.fetchProfile();
            if (account) {
                accountObject = {
                    firstname: account?.firstname,
                    lastname: account?.lastname,
                    email: account?.email1,
                    homePhone: account?.homePhone,
                    optIn: account?.optInEmail,
                };
            }
        } catch (e) {
            this.logger.error('Error fetching the profile information', e);
        }

        const updatedTradeInModel = new TradeInModel({
            tradeInVehicle,
            estimation: {
                amount,
                amountWasUpdated: true,
                high: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.high || 0,
                low: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.low || 0,
            },
            profile: existingAccount ? accountObject : tradeInVehicle.contact,
        });
        return TradeInService.saveTradeInNew(updatedTradeInModel, this.props.quote, true, this.props.config, false);
    }

    // For Dilawri version use this function to update the trade-in lead
    async updateLeadDilawriVersion(amount, selectedOption, existingAccount, asUniqueLead) {
        const { tradeInVehicle } = this.state;
        let accountObject = {};
        try {
            const account = await ProfileService.fetchProfile();
            if (account) {
                accountObject = {
                    firstname: account?.firstname,
                    lastname: account?.lastname,
                    email: account?.email1,
                    homePhone: account?.homePhone,
                    optIn: account?.optInEmail,
                };
            }
        } catch (e) {
            this.logger.error('Error fetching the profile information', e);
        }

        const tradeOptions = {
            1: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE')}`,
            2: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE')}`,
            3: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE')}`,
        };

        const updatedTradeInModel = new TradeInModelNew({
            tradeInVehicle,
            estimation: {
                amount,
                amountWasUpdated: true,
                high: amount,
                low: amount,
            },
            info:
                LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.SELECTED_TRADE_OPTION', {
                    option: tradeOptions[selectedOption],
                }) +
                // Specify that a email cheque has been sent to client
                (selectedOption === TRADE_OPTIONS.SELL_VEHICLE
                    ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.SELL_VEHICLE_EMAIL_CHEQUE_LEAD')
                    : ''),
            profile: existingAccount ? accountObject : tradeInVehicle.contact,
        });

        return TradeInService.saveTradeInNew(
            updatedTradeInModel,
            this.props.quote,
            false,
            this.state.config,
            asUniqueLead
        );
    }

    handleEstimateChange(amount, existingAccount) {
        const { tradeInVehicle } = this.state;
        this.updateTradeInVehicle({ ...tradeInVehicle, estimation: { ...tradeInVehicle.estimation, amount } });
        return this.updateLeadTrade360Version(amount, existingAccount);
    }

    // Call when update the estimate slider
    handleEstimateChangeSlider(tradeInValue) {
        const { tradeInVehicle } = this.state;
        // First update the token with new estimation
        return ShowroomCBBService.updateTradeVehicleToken(
            tradeInVehicle.tradeVehicleToken,
            tradeInVehicle?.odometer,
            tradeInVehicle?.exteriorColor?.description,
            tradeInVehicle?.interiorColor?.description,
            null,
            tradeInVehicle?.vin,
            null,
            true, // noValuation - Do not call valuation endpoint for this call
            tradeInValue, // Slider updated estimation value
            this.props.config
        )
            .then((result) => {
                const tradeVehicleToken = LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.TOKEN_LEAD', {
                    token: result.tradeVehicleToken,
                });

                // Update estimation and tradeInExtraInfo with new valuation amount
                const tradeVehicleEstimation = {
                    ...tradeInVehicle.estimation,
                    ...{
                        amount: tradeInValue,
                        tradeInExtraInfo: {
                            ...tradeInVehicle.estimation.tradeInExtraInfo,
                            estimation: {
                                ...tradeInVehicle.estimation.tradeInExtraInfo.estimation,
                                amount: tradeInValue,
                            },
                        },
                    },
                };

                const updatedTradeInVehicle = {
                    details: TradeInVehicleService.getVehicleOptionsList(tradeInVehicle) + tradeVehicleToken,
                    estimation: tradeVehicleEstimation,
                    tradeVehicleToken: result.tradeVehicleToken,
                };

                this.updateTradeInVehicle({
                    ...tradeInVehicle,
                    ...updatedTradeInVehicle,
                });
                return Promise.resolve();
            })
            .catch((err) => {
                this.logger.error('Error Updating the estimation value', err);
                return Promise.reject();
            });
    }

    saveTradeIn({ existingAccount = false, tradeInValues }) {
        return this.handleEstimateChange(
            UtilsService.roundValueToStepIntoRange(
                tradeInValues.amount,
                SLIDER.STEP_VALUE,
                tradeInValues.low,
                tradeInValues.high
            ),
            existingAccount
        )
            .then(() => {
                this.dispatchTradeInCreatedEvent();
                PurchaseMethodsActions.setTradeIn(tradeInValues);
                return Promise.resolve();
            })
            .catch(() => {
                return Promise.reject();
            });
    }

    displayCreatedOnDate() {
        if (AccountService.isAuthenticated() && this.props.tradeInVehicle?.creationDate) {
            const creationDate = moment(this.props.tradeInVehicle.creationDate)
                .locale(LocaleService.getSimpleLocale())
                .format(LocaleService.t('COMMONS_GENERAL.DATE_FORMAT.FULL'));
            return (
                <div className={WEB_CSS_CLASSNAMES.BODY2}>
                    {LocaleService.t('TRADE_IN.CREATED_ON', { date: creationDate })}
                </div>
            );
        }
        return null;
    }

    render() {
        if (this.state.isLoading) return <div className="loading" />;

        const steps = this.getSteps(this.context.version, this.props.isModal);
        const tradeInNavigationSteps = this.getNavigationSteps(this.context.version);
        const progressMargin = 100 / tradeInNavigationSteps.length / 2;
        const isTrade360 = this.context.version === WIDGET_VERSION.TRADE_360;
        const quoteVehicleInfo = (isTrade360 && this.props.quote?.vehicle?.getYearMakeModelTrim()) || '';

        const renderModalTitle = () => {
            if (isTrade360) {
                const { isViewOnly, fieldsLocked, useCBB, skipEstimate, officialEdit } = this.context || {};
                const displayViewTitle = isViewOnly || fieldsLocked;
                return (
                    <>
                        {this.getStepName(this.state.index) === 'CBB_ESTIMATE' ? (
                            <div className={WEB_CSS_CLASSNAMES.HEADING2}>
                                {useCBB && !skipEstimate && !officialEdit
                                    ? LocaleService.t(
                                          `TRADE_IN.MODAL_TITLE_CBB_ESTIMATED_STEP${displayViewTitle ? '_VIEW' : ''}`
                                      )
                                    : LocaleService.t(`TRADE_IN.MODAL_TITLE${displayViewTitle ? '_VIEW' : ''}`)}
                            </div>
                        ) : (
                            <div className={WEB_CSS_CLASSNAMES.HEADING2}>
                                {useCBB && !skipEstimate && !officialEdit
                                    ? LocaleService.t(`TRADE_IN.MODAL_TITLE_CBB${displayViewTitle ? '_VIEW' : ''}`)
                                    : LocaleService.t(`TRADE_IN.MODAL_TITLE${displayViewTitle ? '_VIEW' : ''}`)}
                            </div>
                        )}
                        {fieldsLocked && this.displayCreatedOnDate()}
                    </>
                );
            }
            return <h4>{LocaleService.t('TRADE_IN.DILAWRI_MODAL_TITLE')}</h4>;
        };
        const hasClassTrade360 = isTrade360 ? 'trade-360' : '';
        return (
            <div
                className={`${hasClassTrade360} TradeIn__wrapper TradeIn__wrapper--step-${this.getStepName(
                    this.state.index
                )}`}
                id="webauto-trade-in-modal"
            >
                <Checkmark />
                <LoaderFullScreen show={this.state.isScreenLock} message={LocaleService.t('TRADE_IN.SCREEN_LOCKED')}>
                    <>
                        <div className={`steps-header header-${this.state.isHeaderShown ? 'shown' : 'hidden'}`}>
                            {renderModalTitle()}
                            {!isTrade360 && (
                                <>
                                    <StepBar
                                        currentStep={this.state.index || 0}
                                        onClick={this.onClickStep}
                                        steps={tradeInNavigationSteps}
                                    />
                                    <div style={{ margin: `0 ${progressMargin}%` }}>
                                        <ProgressBar
                                            max={tradeInNavigationSteps.length - 1}
                                            min={0}
                                            amount={this.state.index || 0}
                                        />
                                    </div>
                                </>
                            )}
                            {!isTrade360 &&
                                this.state.index < tradeInNavigationSteps.length && (
                                    <div className="steps-header--bottom">
                                        <span className="steps-header--bottom--currentStep">
                                            {tradeInNavigationSteps?.[this.state.index]?.name}
                                        </span>
                                        <span className="steps-header--bottom--progress">
                                            {`${this.state.index + 1}/${tradeInNavigationSteps.length}`}
                                        </span>
                                    </div>
                                )}
                            {isTrade360 && (
                                <VehicleTypeLabel
                                    vehicleLabel={LocaleService.t('TRADE_IN.TITLES.VEHICLE_OF_INTEREST')}
                                    vehicleInfo={quoteVehicleInfo}
                                />
                            )}
                            {isTrade360 && (
                                <StepsHeader
                                    currentStepIndex={this.state.index}
                                    useCBB={
                                        this.context.useCBB && !this.context.skipEstimate && !this.context.officialEdit
                                    }
                                    isModal={this.props.isModal}
                                />
                            )}
                        </div>
                        <Wizard
                            steps={steps}
                            parentSelector=".TradeIn__wrapper"
                            startingStepIndex={this.state.index || 0}
                        />
                        {this.props.isModal &&
                            this.state.isModalCloseShown && (
                                <button
                                    type="button"
                                    className="uk-button uk-button--close"
                                    id="close-modal-button"
                                    onClick={() => this.props.closeModal({ confirm: this.state.displayConfirmation })}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            )}
                    </>
                </LoaderFullScreen>
                {this.context.version !== WIDGET_VERSION.DILAWRI && (
                    <img
                        src={`${this.imagesServer}/images/manufacturer/blackbook/CBB-POWERED-BY.png`}
                        alt="Black Book"
                        className="blackbook-logo"
                        width="200"
                    />
                )}
            </div>
        );
    }
}

InstantEstimateWrapper.defaultProps = {
    isModal: true,
    closeModal: noop,
    config: {},
    isMultiDealerTradeIn: false,
    sessionUserGeoData: {},
};

InstantEstimateWrapper.propTypes = {
    tradeInVehicle: PropTypes.object.isRequired,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
    isModal: PropTypes.bool,
    closeModal: PropTypes.func,
    onTradeInSuccess: PropTypes.func.isRequired,
    appointmentEnabled: PropTypes.bool.isRequired,
    isMultiDealerTradeIn: PropTypes.bool.isRequired,
    config: PropTypes.object,
    sessionUserGeoData: PropTypes.object,
};

InstantEstimateWrapper.displayName = 'InstantEstimateWrapper';

export default InstantEstimateWrapper;
